import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, FileText, BarChart3, ShieldCheck, Radio, BookOpen, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { COUNTRIES } from "@/data/countries";

const PROGRAMMES = ["CSCE", "CSP", "CSL", "CCSO", "PGDCSL"];

const BENEFITS = [
  { icon: Users, title: "Cohort onboarding in minutes", text: "Send us your team list and every member is registered, enrolled and welcomed with their login details on the same day." },
  { icon: FileText, title: "Purchase-order invoicing", text: "One invoice against your PO. No individual card payments, no expense claims, no payment checks for your staff." },
  { icon: BarChart3, title: "Live progress reports", text: "A private, read-only link shows who has signed in, sat the exam, passed and received their certificate, updated live and emailed weekly." },
  { icon: Radio, title: "Live sessions and secure exams", text: "Faculty-led live sessions for CSCE and CSP, and secure 3-hour online examinations with a transparent 70% pass mark." },
  { icon: BookOpen, title: "Official study guides", text: "Every learner downloads the official CSI study guide, aligned with CSF-STRAT and the GCSB standards (Version 2.0)." },
  { icon: ShieldCheck, title: "Independently verifiable credentials", text: "CSI is a GCSB Accredited Partner and Centre of Excellence. Every credential can be verified on the GCSB public register." },
];

const STEPS = ["Request a proposal", "Agree scope and pricing", "Issue your PO", "We onboard your cohort", "Track progress live"];

const ProposalForm = () => {
  const [f, setF] = useState({ company: "", contact_name: "", email: "", country: "", programmes: [], staff_count: 5, message: "" });
  const [state, setState] = useState({ busy: false, done: false });
  const toggle = (p) => setF({ ...f, programmes: f.programmes.includes(p) ? f.programmes.filter((x) => x !== p) : [...f.programmes, p] });
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: false });
    try { await api.post("/proposals", { ...f, staff_count: Number(f.staff_count) || 1 }); setState({ busy: false, done: true }); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); setState({ busy: false, done: false }); }
  };
  if (state.done) return (
    <div className="card-warm p-6" data-testid="proposal-success">
      <h3 className="font-display text-2xl">Thank you - request received</h3>
      <p className="text-sm text-zinc-700 mt-2">Our team will reply within 2 working days with a tailored proposal. A confirmation has been sent to {f.email}.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="card-warm p-5 sm:p-6 space-y-3" data-testid="proposal-form">
      <h3 className="font-display text-2xl">Request a proposal</h3>
      <div className="grid sm:grid-cols-2 gap-3 [&>*]:min-w-0 [&>*]:w-full">
        <input className="field" required placeholder="Company / organisation" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="proposal-company"/>
        <input className="field" required placeholder="Contact name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="proposal-name"/>
        <input className="field" required type="email" placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="proposal-email"/>
        <select className="field" required value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="proposal-country">
          <option value="">Country…</option>
          {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <div className="text-sm font-semibold mb-1">Programmes of interest</div>
        <div className="flex flex-wrap gap-2">
          {PROGRAMMES.map((p) => (
            <button type="button" key={p} onClick={() => toggle(p)} className={`chip text-xs ${f.programmes.includes(p) ? "bg-[var(--navy)] text-white border-[var(--navy)]" : ""}`} data-testid={`proposal-prog-${p}`}>{p}</button>
          ))}
        </div>
      </div>
      <label className="block text-sm font-semibold">Number of staff
        <input className="field mt-1 w-full" type="number" min={1} required value={f.staff_count} onChange={(e) => setF({ ...f, staff_count: e.target.value })} data-testid="proposal-staff"/>
      </label>
      <textarea className="field w-full" rows={4} placeholder="Goals, timelines or anything else we should know (optional)" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="proposal-message"/>
      <button disabled={state.busy} className="btn-terracotta px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="proposal-submit">
        <Send size={14}/> {state.busy ? "Sending…" : "Request proposal"}
      </button>
    </form>
  );
};

export default function OrganisationsPage() {
  useSeo({
    title: "For Organisations - Team Certification in Converged Security",
    description: "Certify your security teams with CSI: cohort onboarding, purchase-order invoicing, live progress reports and GCSB-accredited CSCE, CSP, CSL, CCSO and PGDCSL programmes.",
    path: "/organisations",
  });
  return (
    <div data-testid="organisations-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="eyebrow mb-2">For organisations</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">Certify your whole security team, with one partner and one invoice.</h1>
        <p className="text-base text-zinc-700 mt-4 max-w-3xl">CSI helps banks, utilities, healthcare, public bodies and enterprises build converged security capability across cyber, physical and governance teams, with GCSB-accredited certifications and simple purchase-order invoicing.</p>
        <div className="flex flex-wrap gap-3 mt-6">
          <a href="#proposal" className="btn-terracotta px-6 py-3 rounded-full text-sm font-semibold" data-testid="org-cta-proposal">Request a proposal</a>
          <Link to="/courses" className="chip px-6 py-3 text-sm" data-testid="org-cta-programmes">View programmes</Link>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {BENEFITS.map(({ icon: Icon, title, text }) => (
          <div key={title} className="card-warm p-5" data-testid={`org-benefit-${title.split(" ")[0].toLowerCase()}`}>
            <Icon size={20} className="text-[var(--terracotta)]"/>
            <h3 className="font-display text-xl mt-2">{title}</h3>
            <p className="text-sm text-zinc-700 mt-1">{text}</p>
          </div>
        ))}
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="eyebrow mb-3">How it works</div>
        <ol className="grid sm:grid-cols-5 gap-3">
          {STEPS.map((s, i) => (
            <li key={s} className="rounded-2xl border border-[var(--border-warm)] bg-white p-4 text-sm"><span className="font-display text-2xl text-[var(--terracotta)]">{i + 1}</span><div className="font-semibold mt-1">{s}</div></li>
          ))}
        </ol>
      </section>
      <section id="proposal" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-[1fr_1.2fr] gap-8 items-start">
        <div>
          <div className="eyebrow mb-2">Volume pricing</div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight">Volume pricing available - request a proposal</h2>
          <p className="text-sm text-zinc-700 mt-3">Tell us about your team and the programmes you are considering. We will reply within 2 working days with a tailored proposal covering pricing, delivery schedule and purchase-order invoicing.</p>
          <p className="text-sm text-zinc-700 mt-3">Prefer email? Write to <a href="mailto:info@convergedsecurity.es" className="underline">info@convergedsecurity.es</a>.</p>
        </div>
        <ProposalForm/>
      </section>
    </div>
  );
}
